import log from 'embed_v2/helpers/log'
import dayjs from 'embed_v2/services/dayjs'
import appEmitter from 'embed_v2/services/emitter'
import StyleSheetManager from 'embed_v2/services/style_sheet_manager'
import Validation from 'embed_v2/services/validation'
import Version, { ELEMENT_TYPE, WidgetElement, EmbedVersion } from 'embed_v2/types/version'
import uniq from 'lodash/uniq'
import { type Emitter, createNanoEvents } from 'nanoevents'
import qs from 'query-string'
import { validate as uuidValidate } from 'uuid'
import WebFont from 'webfontloader'
import Secrets from 'Secrets'
import type { PageItemsInterface } from './pageItems'
import type { ChallengePayload } from './services/challenge'
import type Viewer from 'embed_v2/types/viewer'

let timeZone = dayjs.tz.guess()
if (!timeZone || timeZone.toLowerCase() === 'etc/unknown' || timeZone.startsWith('+')) {
  timeZone = 'UTC'
}

type SubscribePayload = ChallengePayload & {
  version_id: number
  viewer: object
  token: string
}

type UpdatePayload = {
  cid: string
  phone?: string
  time_zone: string
}

type SubscribeSuccessResponse = {
  cid: string
  email: string
  first_name: string
  last_name: string
  phone: string
  redirect_url: string
  custom_fields: object
}

type SubscribeErrorResponse = {
  errors: string
}

type TimerValue = {
  days: string
  hours: string
  minutes: string
  seconds: string
  totalSeconds: number
}

export type API = {
  item: Item
  onSubmit: (callback: (payload: object) => void) => void
  onLeadSubmit: (callback: (payload: object) => void) => void
  onTimerTick: (callback: (api: API, time: TimerValue) => void) => void
  show: () => void
  hide: () => void
  setField: (field: any, value: any) => void
}

export type Item = {
  id: string
  uniqId?: string
  versionId?: number
  onReady?: (value: API) => void
  cid?: string
  targetElement?: Element | string
  hash?: string
  ab?: string
  disableHandler?: boolean
  source?: string
  timeZone?: string
  stepSlug?: string
  embedVersion?: EmbedVersion
  preview?: boolean
  cfTimerId?: string
  email?: string
  first_name?: string
  last_name?: string
  forwardCid?: boolean
}

class ItemApi {
  item: Item
  context: PageItemsInterface
  emitter: Emitter
  validation: Validation
  onReady: () => void
  onLeadSubmit: (payload: object) => void
  onSubmit: (payload: object) => void
  onTimerTick: (time: TimerValue) => void
  _node?: Element
  callbacks: {
    onSubmit: ((payload: object) => void)[]
    onLeadSubmit: ((payload: object) => void)[]
    onTimerTick: ((api: API, time: TimerValue) => void)[]
  }

  clickfunnels: {
    cfTimerSections?: NodeListOf<Element>
    cfTimerElementsV2?: NodeListOf<Element>
  }

  constructor(item: Item, context: PageItemsInterface) {
    this.context = context
    log.info('#constructor', item)
    this.emitter = createNanoEvents()
    this.item = item
    this.validation = new Validation()
    this.callbacks = {
      onSubmit: [],
      onLeadSubmit: [],
      onTimerTick: []
    }
    this.onReady = () => {
      log.info('#onReady', item.onReady)
      if (typeof item.onReady === 'function') {
        item.onReady(this.api)
      }
    }
    this.onLeadSubmit = payload => {
      log.info('#onLeadSubmit', payload, this.callbacks.onLeadSubmit)
      this.callbacks.onLeadSubmit.forEach(callback => {
        callback(payload)
      })
    }
    this.onSubmit = payload => {
      log.info('#onSubmit', payload, this.callbacks.onSubmit)
      this.callbacks.onSubmit.forEach(callback => {
        callback(payload)
      })
    }
    this.onTimerTick = time => {
      log.debug('#onTimerTick', time, this.callbacks.onTimerTick, this.item)
      if (this.item.cfTimerId) {
        this.cfUpdateTimer(time)
      }

      this.callbacks.onTimerTick.forEach(callback => {
        callback(this.api, time)
      })
    }
    this.clickfunnels = {}

    this.show = this.show.bind(this)
    this.hide = this.hide.bind(this)
    this.setField = this.setField.bind(this)
  }

  set node(val) {
    this._node = val
  }

  get node() {
    return this._node
  }

  get uniqId() {
    const uniqIds = [this.id]
    if (this.hash?.length) {
      uniqIds.push(this.hash)
    }
    return uniqIds.join('-')
  }

  get id() {
    return this.item.id
  }

  get cid() {
    return this.item.cid
  }

  get targetElement() {
    return this.item.targetElement
  }

  get hash() {
    return this.item.hash
  }

  get ab() {
    return this.item.ab
  }

  get disableHandler() {
    return this.item.disableHandler
  }

  get source() {
    return this.item.source
  }

  get timeZone() {
    return this.item.timeZone || timeZone
  }

  get stepSlug() {
    return this.item.stepSlug
  }

  get embedVersion() {
    return this.item.embedVersion
  }

  get preview() {
    return !!this.item.preview
  }

  get predefinedViewer() {
    const viewer: Viewer = {}
    const parsedQuery = qs.parse(window.location.search)
    const email = this.item.email || [parsedQuery.email].flat()[0]
    const first_name = this.item.first_name || parsedQuery.first_name || ''
    const last_name = this.item.last_name || parsedQuery.last_name || ''

    if (email) {
      viewer.email = email
    }
    if (first_name || last_name) {
      viewer.name = `${first_name} ${last_name}`.trim()
    }

    return viewer
  }

  get mainElementId() {
    return `wf_main_${this.uniqId}`
  }

  get api(): API {
    return {
      item: this.item,
      onSubmit: callback => {
        this.callbacks.onSubmit.push(callback)
      },
      onLeadSubmit: callback => {
        this.callbacks.onLeadSubmit.push(callback)
      },
      onTimerTick: callback => {
        this.callbacks.onTimerTick.push(callback)
      },
      show: this.show,
      hide: this.hide,
      setField: this.setField
    }
  }

  on(event: string, callback: (...args: any) => void) {
    return this.emitter.on(event, callback)
  }

  show() {
    this.emitter.emit('show')
  }

  hide() {
    this.emitter.emit('hide')
  }

  setField(field: any, value: any) {
    this.emitter.emit('setField', field, value)
  }

  subscribe(
    payload: SubscribePayload,
    successFn: (value: SubscribeSuccessResponse) => void,
    errorFn: (value: SubscribeErrorResponse) => void
  ) {
    const data = {
      version_id: payload.version_id,
      recaptcha_token: payload.recaptcha_token,
      recaptcha_action: payload.recaptcha_action,
      cloudflare_turnstile_token: payload.cloudflare_turnstile_token,
      viewer: {
        ...payload.viewer,
        token: payload.token
      }
    }

    this.fetchData(`${Secrets.meetingHost}/embed/v2/viewers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(response => response.json())
      .then(response => {
        if (typeof successFn === 'function') {
          successFn(response)
        }
      })
      .catch(error => {
        const errorMsg = error.errors ? error : { errors: "We're sorry, but something went wrong" }
        if (typeof errorFn === 'function') {
          errorFn(errorMsg)
        }
      })
  }

  updateViewer(payload: UpdatePayload) {
    const data = {
      viewer: {
        phone: payload.phone,
        time_zone: payload.time_zone
      }
    }

    return this.fetchData(`${Secrets.meetingHost}/embed/v2/viewers/${payload.cid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then(response => response.json())
  }

  cfUpdateTimer(time: TimerValue) {
    log.debug('#cfUpdateTimer', time, this.clickfunnels)

    const hasTimerSections = this.clickfunnels.cfTimerSections || this.clickfunnels.cfTimerElementsV2

    const timeMap = [time.days, time.hours, time.minutes, time.seconds]

    if (hasTimerSections) {
      if (this.clickfunnels.cfTimerSections) {
        const mapOffset = timeMap.length - this.clickfunnels.cfTimerSections.length

        this.clickfunnels.cfTimerSections.forEach((section, index) => {
          section.innerHTML = timeMap[index + mapOffset]
        })
      }

      if (this.clickfunnels.cfTimerElementsV2) {
        for (const element of this.clickfunnels.cfTimerElementsV2) {
          log.debug('#cfUpdateTimer element', element)
          const sections = element.querySelectorAll('.elCountdownAmountContainer.elCountdownAmount span')
          log.debug('#cfUpdateTimer sections', sections)

          if (sections.length) {
            const mapOffset = timeMap.length - sections.length

            sections.forEach((section, index) => {
              section.innerHTML = timeMap[index + mapOffset]
            })
          }
        }
      }
    } else {
      const cfTimerId = this.item.cfTimerId?.replace('#', '')
      const cfTimerSections = document.querySelectorAll(`#${cfTimerId} .countdown-amount`)
      log.debug('#cfUpdateTimer findElement', cfTimerId, cfTimerSections)

      if (cfTimerSections.length) {
        this.clickfunnels.cfTimerSections = cfTimerSections
      }

      const cfTimerElementsV2 = document.querySelectorAll(`#${cfTimerId}, div[cftimerid="${cfTimerId}"]`)
      log.debug('#cfUpdateTimerV2 findElement', cfTimerId, cfTimerElementsV2)

      if (cfTimerElementsV2.length) {
        this.clickfunnels.cfTimerElementsV2 = cfTimerElementsV2
      }
    }
  }

  fetchData(path: string, data: object) {
    return fetch(path, data).then(res => {
      if (res.ok) {
        return res
      }

      return res.json().then(json => Promise.reject(json))
    })
  }

  injectCss(version: Version) {
    const fontFamilies: string[] = []

    version.steps.forEach(step => {
      step.rows.forEach(row => {
        row.columns.forEach(column => {
          column.elements.forEach(element => {
            this.validation.add(step.id, element)

            fontFamilies.push(...this.getFontFamilies(element))
          })
        })
      })
    })

    log.info('fontFamilies', uniq(fontFamilies))
    const families = uniq(fontFamilies).map(family => `${family}:400,700`)

    if (families.length) {
      WebFont.load({
        active: () => {
          window._wf_fonts_active = true
          appEmitter.emit('webfont-active')
        },
        classes: false,
        google: { families },
        timeout: 2000
      })
    } else {
      window._wf_fonts_active = true
      appEmitter.emit('webfont-active')
    }

    const styleManager = new StyleSheetManager(this.uniqId, version)
    styleManager.inject()
  }

  getFontFamilies(element: WidgetElement) {
    const result: string[] = []

    switch (element.type) {
      case ELEMENT_TYPE.Bullet:
      case ELEMENT_TYPE.Button:
        result.push(element.options.text.font_family)
        result.push(element.options.subtext.font_family)
        break
      case ELEMENT_TYPE.Checkbox:
        result.push(element.options.font_family)
        break
      case ELEMENT_TYPE.Datetime:
        result.push(element.options.list.label.font_family)
        result.push(element.options.list.sublabel.font_family)
        result.push(element.options.dropdown.font_family)
        result.push(element.options.dropdown.menu.label.font_family)
        result.push(element.options.dropdown.menu.sublabel.font_family)
        result.push(element.options.unselectable.font_family)
        break
      case ELEMENT_TYPE.Dropdown:
        result.push(element.options.dropdown.font_family)
        result.push(element.options.dropdown.menu.font_family)
        result.push(element.options.dropdown.menu.label.font_family)
        result.push(element.options.dropdown.menu.sublabel.font_family)
        break
      case ELEMENT_TYPE.Header:
      case ELEMENT_TYPE.Text:
        result.push(element.options.font_family)
        break
      case ELEMENT_TYPE.Hosts:
        result.push(element.options.name.font_family)
        result.push(element.options.role.font_family)
        break
      case ELEMENT_TYPE.Icon:
      case ELEMENT_TYPE.Image:
        break
      case ELEMENT_TYPE.ImageFeature:
        result.push(element.options.head.font_family)
        result.push(element.options.body.font_family)
        break
      case ELEMENT_TYPE.Input:
      case ELEMENT_TYPE.Textarea:
        result.push(element.options.input.text.font_family)
        result.push(element.options.input.label.font_family)
        break
      case ELEMENT_TYPE.Logo:
        result.push(element.options.font_family)
        break
      case ELEMENT_TYPE.Quiz:
        result.push(element.options.label.font_family)
        result.push(element.options.sublabel.font_family)
        break
      case ELEMENT_TYPE.Reminder:
        result.push(element.options.font_family)
        result.push(element.options.phone.text.font_family)
        result.push(element.options.phone.label.font_family)
        break
      case ELEMENT_TYPE.Timer:
        result.push(element.options.value.font_family)
        result.push(element.options.label.font_family)
        break
      case ELEMENT_TYPE.WebinarLink:
        result.push(element.options.text.font_family)
        result.push(element.options.label.font_family)
        result.push(element.options.copy_button.font_family)
        break

      default:
        break
    }

    return result
  }

  getSequenceCid() {
    if (!this.item.forwardCid) return

    try {
      const cid = localStorage.getItem('_wf_cid')

      if (!cid) return null

      if (uuidValidate(cid)) {
        return cid
      }
    } catch {
      return null
    }
  }

  setSequenceCid(cid: string) {
    try {
      localStorage.setItem('_wf_cid', cid)
    } catch {}
  }
}

export default ItemApi
